import {
    AirlineSeatReclineNormal,
    AirportShuttle,
    ChildCare,
    Close,
    GridView,
    LocalBar,
    Mosque,
    OndemandVideo,
    PowerSettingsNew,
    Restaurant,
    Shower,
    SignalCellularAlt,
    Wifi
} from '@mui/icons-material'
import { Box, ButtonBase, Drawer, IconButton, Tooltip } from '@mui/material'
import { useTranslations } from 'next-intl'

import AmenitiesDetailList from './amenitiesDetailList'
import { type TooltipAmenityItem } from './amenitiesDetailList'

export type { TooltipAmenityChild, TooltipAmenityItem } from './amenitiesDetailList'

export interface AircraftAmenitiesProps {
    activeAmenityKeys: string[]
    tooltipItems: TooltipAmenityItem[]
    amenitiesRemarksNote?: string
    showKnowMore?: boolean
    mobileDetailsOpen?: boolean
    onKnowMoreClick?: () => void
    onMobileDetailsClose?: () => void
}

const AMENITY_ICON_MAP = {
    seatType: AirlineSeatReclineNormal,
    seatLayout: GridView,
    meals: Restaurant,
    beverages: LocalBar,
    wifi: Wifi,
    powerType: PowerSettingsNew,
    avod: OndemandVideo,
    infantCare: ChildCare,
    mobile: SignalCellularAlt,
    prayerArea: Mosque,
    provideTransportation: AirportShuttle,
    showerArea: Shower
} as const

const AircraftAmenities = ({
    activeAmenityKeys,
    tooltipItems,
    amenitiesRemarksNote,
    showKnowMore = false,
    mobileDetailsOpen = false,
    onKnowMoreClick,
    onMobileDetailsClose
}: AircraftAmenitiesProps) => {
    const t = useTranslations('common')
    if (activeAmenityKeys.length === 0) return null

    const tooltipTitle = (
        <AmenitiesDetailList
            items={tooltipItems}
            amenitiesRemarksNote={amenitiesRemarksNote}
            listSx={{ maxHeight: 400, overflowY: 'auto' }}
        />
    )

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap', mt: { xs: 1, lg: 0 } }}>
                {activeAmenityKeys.map(amenityKey => {
                    const IconComponent = AMENITY_ICON_MAP[amenityKey as keyof typeof AMENITY_ICON_MAP]
                    if (!IconComponent) return null

                    const chip = (
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-flex',
                                width: { xs: 20, lg: 24 },
                                height: { xs: 20, lg: 24 },
                                borderRadius: 1,
                                backgroundColor: 'grey.100',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <IconComponent sx={{ fontSize: { xs: 14, lg: 16 }, color: 'text.primary' }} />
                        </Box>
                    )

                    return (
                        <Tooltip
                            key={amenityKey}
                            title={tooltipTitle}
                            placement="top"
                            arrow
                            disableHoverListener={showKnowMore}
                            disableFocusListener={showKnowMore}
                            disableTouchListener
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        bgcolor: '#08172C',
                                        color: 'common.white',
                                        fontSize: 12,
                                        fontWeight: 400,
                                        maxWidth: 280,
                                        overflow: 'visible',
                                        p: 1.5,
                                        boxShadow: 3
                                    }
                                },
                                arrow: {
                                    sx: { color: '#08172C' }
                                }
                            }}
                        >
                            {chip}
                        </Tooltip>
                    )
                })}
                {showKnowMore && (
                    <ButtonBase
                        type="button"
                        onClick={onKnowMoreClick}
                        sx={{
                            color: 'primary.main',
                            fontSize: 10,
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            display: 'inline-flex',
                            borderRadius: 0.5,
                            px: 0.5,
                            minHeight: 'auto',
                            '&:focus-visible': { outlineOffset: 2 }
                        }}
                    >
                        {t('knowMore')}
                    </ButtonBase>
                )}
            </Box>

            {showKnowMore && (
                <Drawer
                    anchor="bottom"
                    open={mobileDetailsOpen}
                    onClose={onMobileDetailsClose}
                    slotProps={{
                        paper: {
                            sx: {
                                bgcolor: 'common.white',
                                color: 'text.primary',
                                borderTopLeftRadius: 16,
                                borderTopRightRadius: 16,
                                maxHeight: '70vh',
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden'
                            }
                        }
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            flexShrink: 0,
                            px: 1,
                            pt: 1,
                            pb: 0.5
                        }}
                    >
                        <IconButton
                            type="button"
                            onClick={onMobileDetailsClose}
                            aria-label={t('close')}
                            size="small"
                            sx={{ color: 'text.primary' }}
                        >
                            <Close sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Box>
                    <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 2 }}>
                        <AmenitiesDetailList items={tooltipItems} amenitiesRemarksNote={amenitiesRemarksNote} />
                    </Box>
                </Drawer>
            )}
        </>
    )
}

export default AircraftAmenities
